import type { IPublicClientApplication } from "@azure/msal-browser";
import { BACKEND_URL } from "src/hooks/useMetadata";
import { SEARCH_MAX_RESULTS } from "src/hooks/useSearchAfterPagination";
import { postAPI } from "src/utils/fetch";
import type { BucketSelection, FacetFilter } from "../../types";
import type { AssetRecord } from "../graph/buildAssetDocsGraph";
import { SUB_FACETS_STALE_TIME } from "../subFacetsQuery";
import type { AssetProfile } from "./assetProfiles";
import { toAssetRecords } from "./toAssetRecords";

export const ASSET_MAX_RESULT = SEARCH_MAX_RESULTS;

export const fetchAssets = async ({
    instance,
    index,
    profile,
    filters,
    dateFilters = [],
}: {
    instance: IPublicClientApplication;
    index: string;
    profile: AssetProfile;
    filters: BucketSelection[];
    dateFilters?: FacetFilter[];
}): Promise<AssetRecord[]> => {
    const url = new URL(`v1/search/${index}/metadata/docs`, BACKEND_URL);
    url.searchParams.set("max_result", String(ASSET_MAX_RESULT));
    const response = await postAPI({
        url: url.toString(),
        instance,
        payload: [
            ...filters.map((filter) => ({ key: filter.field, value: filter.value })),
            ...dateFilters,
        ] as unknown as Record<string, unknown>[],
    });
    return toAssetRecords({ body: await response.json(), profile });
};

export const assetsQueryOptions = ({
    instance,
    index,
    profile,
    selected,
    dateFilters = [],
}: {
    instance: IPublicClientApplication;
    index: string;
    profile: AssetProfile;
    selected?: BucketSelection;
    dateFilters?: FacetFilter[];
}) => ({
    queryKey: [
        "overview-assets",
        index,
        selected?.field ?? "",
        selected?.value ?? "",
        ...dateFilters.flatMap((filter) => [filter.key, ...filter.value]),
    ],
    queryFn: () =>
        fetchAssets({
            instance,
            index,
            profile,
            filters: selected ? [selected] : [],
            dateFilters,
        }),
    staleTime: SUB_FACETS_STALE_TIME,
});
